import React from 'react';
import { Loader2, X, Check, Plug } from 'lucide-react';
import { moduleApi } from '../../lib/api';

type AppKey = 'stripe' | 'revolut';

interface AppDef {
    key: AppKey;
    name: string;
    description: string;
    logo: React.ReactNode;
    settingsKey: 'stripe_secret_key' | 'revolut_secret_key';
    fieldLabel: string;
    fieldPlaceholder: string;
    helpText: string;
    hasSandboxToggle?: boolean;
}

const StripeLogo = () => (
    <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: '#635BFF' }}>
        <span className="text-white font-black text-sm tracking-tight">S</span>
    </div>
);

const RevolutLogo = () => (
    <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: '#000000' }}>
        <span className="text-white font-black text-sm tracking-tight">R</span>
    </div>
);

const APPS: AppDef[] = [
    {
        key: 'stripe',
        name: 'Stripe',
        description: "Ajoute automatiquement un lien de paiement en ligne dans vos emails de facture et vos demandes de paiement (acompte ou intégral) via Démarches client.",
        logo: <StripeLogo />,
        settingsKey: 'stripe_secret_key',
        fieldLabel: 'Clé secrète Stripe',
        fieldPlaceholder: 'sk_live_... ou sk_test_...',
        helpText: "Trouvable dans votre tableau de bord Stripe, sous Développeurs > Clés API.",
    },
    {
        key: 'revolut',
        name: 'Revolut Business',
        description: "Alternative à Stripe pour les mêmes liens de paiement (factures, acomptes/paiements Démarches client) — nécessite un compte Revolut Business avec Compte Marchand activé.",
        logo: <RevolutLogo />,
        settingsKey: 'revolut_secret_key',
        fieldLabel: 'Clé secrète API Revolut',
        fieldPlaceholder: 'sk_...',
        helpText: "Trouvable dans votre tableau de bord Revolut Business, sous Merchant > Paramètres API.",
        hasSandboxToggle: true,
    },
];

/** Onglet "Application" des Paramètres — hub de connexion aux outils tiers (Stripe pour l'instant,
 *  d'autres viendront ensuite : chaque entrée d'APPS suffit à en brancher une nouvelle). La clé/valeur
 *  de chaque app est stockée dans billing_settings (même table que la Facturation) — on relit toujours
 *  l'objet complet avant de sauvegarder pour ne jamais écraser les autres champs de ce même enregistrement. */
export const ApplicationsSettings: React.FC = () => {
    const [settings, setSettings] = React.useState<Record<string, any>>({});
    const [loading, setLoading] = React.useState(true);
    const [openApp, setOpenApp] = React.useState<AppDef | null>(null);
    const [fieldValue, setFieldValue] = React.useState('');
    const [sandboxValue, setSandboxValue] = React.useState(false);
    const [saving, setSaving] = React.useState(false);
    const [error, setError] = React.useState('');

    const load = () => {
        moduleApi.getBillingSettings().then((s: any) => setSettings(s || {})).catch((e: any) => console.error('Failed to load applications settings', e)).finally(() => setLoading(false));
    };

    React.useEffect(() => { load(); }, []);

    const openModal = (app: AppDef) => {
        setError('');
        setFieldValue(settings[app.settingsKey] || '');
        setSandboxValue(!!settings.revolut_sandbox);
        setOpenApp(app);
    };

    const handleSave = async () => {
        if (!openApp) return;
        setSaving(true);
        setError('');
        try {
            const payload: Record<string, any> = { ...settings, [openApp.settingsKey]: fieldValue.trim() };
            if (openApp.hasSandboxToggle) payload.revolut_sandbox = sandboxValue;
            // Le prestataire tout juste connecté devient le prestataire actif — cohérent avec l'intention
            // de l'établissement à cet instant précis (il vient de le configurer, il a probablement
            // l'intention de s'en servir). Reste modifiable ensuite via le sélecteur si les deux sont connectés.
            if (fieldValue.trim()) payload.active_payment_provider = openApp.key;
            await moduleApi.saveBillingSettings(payload);
            setSettings(payload);
            setOpenApp(null);
        } catch (e: any) {
            setError(e?.message || 'Erreur lors de la sauvegarde.');
        } finally {
            setSaving(false);
        }
    };

    const setActiveProvider = async (provider: AppKey) => {
        setSaving(true);
        try {
            const payload = { ...settings, active_payment_provider: provider };
            await moduleApi.saveBillingSettings(payload);
            setSettings(payload);
        } catch (e: any) {
            console.error('Failed to set active payment provider', e);
        } finally {
            setSaving(false);
        }
    };

    const handleDisconnect = async (app: AppDef) => {
        if (!confirm(`Déconnecter ${app.name} ?`)) return;
        setSaving(true);
        try {
            const payload = { ...settings, [app.settingsKey]: '' };
            await moduleApi.saveBillingSettings(payload);
            setSettings(payload);
        } catch (e: any) {
            console.error('Failed to disconnect app', e);
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-content-muted" size={24} /></div>;
    }

    const connectedProviders = APPS.filter((app) => (app.key === 'stripe' || app.key === 'revolut') && !!(settings[app.settingsKey] || '').trim());

    return (
        <div className="max-w-2xl space-y-4">
            <p className="text-xs text-content-muted italic">Connectez des outils tiers à votre établissement. D'autres applications viendront s'ajouter ici prochainement.</p>

            {connectedProviders.length > 1 && (
                <div className="p-4 rounded-2xl bg-surface-soft border border-border space-y-2">
                    <p className="text-xs font-bold text-content">Moyen de paiement actif pour les liens envoyés (factures, Démarches client)</p>
                    <div className="flex gap-2">
                        {connectedProviders.map((app) => (
                            <button key={app.key} onClick={() => setActiveProvider(app.key)} disabled={saving}
                                className={`px-3 py-2 rounded-xl text-xs font-bold border transition-all ${settings.active_payment_provider === app.key ? 'bg-brand-600 border-brand-600 text-brand-fg' : 'border-border text-content-muted hover:text-content'}`}>
                                {app.name}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {APPS.map((app) => {
                const isConnected = !!(settings[app.settingsKey] || '').trim();
                return (
                    <div key={app.key} className="flex items-center gap-4 p-4 rounded-2xl bg-surface-soft border border-border">
                        {app.logo}
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                                <p className="font-bold text-content text-sm">{app.name}</p>
                                {isConnected && (
                                    <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase px-2 py-0.5 rounded-lg bg-emerald-500/10 text-emerald-500 border border-emerald-500/20">
                                        <Check size={10} /> Connecté
                                    </span>
                                )}
                            </div>
                            <p className="text-xs text-content-muted mt-0.5">{app.description}</p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                            {isConnected && (
                                <button onClick={() => handleDisconnect(app)} disabled={saving} className="px-3 py-2 rounded-xl border border-border text-content-muted hover:text-red-400 hover:border-red-500/30 text-xs font-bold transition-all disabled:opacity-50">
                                    Déconnecter
                                </button>
                            )}
                            <button onClick={() => openModal(app)} className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg text-xs font-bold transition-all">
                                <Plug size={13} /> {isConnected ? 'Modifier' : 'Connecter'}
                            </button>
                        </div>
                    </div>
                );
            })}

            {openApp && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
                    <div className="bg-white dark:bg-surface border border-border rounded-2xl w-full max-w-md shadow-2xl overflow-hidden">
                        <div className="px-6 py-4 border-b border-border flex items-center justify-between">
                            <h3 className="text-lg font-bold text-slate-900 dark:text-content flex items-center gap-2">
                                {openApp.logo} Connecter {openApp.name}
                            </h3>
                            <button onClick={() => setOpenApp(null)} className="text-content-muted hover:text-gray-700 dark:hover:text-slate-900 transition-colors">
                                <X size={20} />
                            </button>
                        </div>
                        <div className="p-6 space-y-4">
                            <div className="space-y-1.5">
                                <label className="text-xs font-bold text-content-muted uppercase tracking-widest">{openApp.fieldLabel}</label>
                                <input
                                    type="password"
                                    autoFocus
                                    value={fieldValue}
                                    onChange={(e) => setFieldValue(e.target.value)}
                                    placeholder={openApp.fieldPlaceholder}
                                    className="w-full bg-surface-soft border border-border rounded-xl px-4 py-3 text-sm text-content outline-none focus:border-brand-500 transition-colors font-mono"
                                />
                                <p className="text-xs text-content-muted">{openApp.helpText}</p>
                            </div>
                            {openApp.hasSandboxToggle && (
                                <label className="flex items-center justify-between cursor-pointer p-3 rounded-xl bg-surface-soft border border-border">
                                    <span className="text-xs font-bold text-content">Mode test (sandbox)</span>
                                    <input type="checkbox" checked={sandboxValue} onChange={(e) => setSandboxValue(e.target.checked)} className="accent-brand-600 w-4 h-4" />
                                </label>
                            )}
                            {error && <p className="text-xs text-red-400 font-semibold">{error}</p>}
                            <div className="flex gap-3 pt-2">
                                <button onClick={() => setOpenApp(null)} disabled={saving} className="flex-1 px-4 py-3 rounded-xl border border-border text-slate-700 dark:text-content-muted font-bold hover:bg-gray-50 dark:hover:bg-surface-soft transition-colors disabled:opacity-60">
                                    Annuler
                                </button>
                                <button onClick={handleSave} disabled={saving} className="flex-1 px-4 py-3 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg font-bold disabled:opacity-50 transition-colors">
                                    {saving ? 'Enregistrement...' : 'Enregistrer'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
